import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Share2, Copy, Send, X, Eye, CheckCircle2, Loader2, Ban } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "");

function StatusChip({ status }) {
  if (!status) return null;
  const base = "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold";
  let el;
  if (status.state === "viewed")
    el = <span className={`${base} bg-[#DBEAFE] text-[#1E40AF]`} data-testid="doc-notice-chip"><Eye size={12} /> Viewed {fmt(status.viewed_at)}</span>;
  else if (status.state === "sent")
    el = <span className={`${base} bg-[#ECFDF5] text-[#177245]`} data-testid="doc-notice-chip"><CheckCircle2 size={12} /> Sent {fmt(status.sent_at)}</span>;
  else
    el = <span className={`${base} bg-[#F1F5F9] text-[#64748B]`} data-testid="doc-notice-chip">Not shared</span>;
  return (
    <span className="inline-flex items-center gap-2">
      {el}
      {status.revoked && <span className={`${base} bg-[#FEE2E2] text-[#B91C1C]`} data-testid="doc-notice-revoked">Link revoked</span>}
    </span>
  );
}

export function DocumentationNotice({ propertyId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [modal, setModal] = useState(null); // {recipient, subject, message, share_url}
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/documentation-notice`);
      setData(data);
    } catch (e) { /* not eligible / not found */ }
    finally { setLoaded(true); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const openPreview = useCallback(async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/properties/${propertyId}/documentation-notice`);
      setModal({ recipient: d.recipient || "", subject: d.subject, message: d.message, share_url: d.share_url });
      setData((c) => ({ ...(c || {}), status: d.status }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't prepare the message."); }
    finally { setBusy(false); }
  }, [propertyId]);

  // Allow an external trigger (e.g. an actions-row button) to open the preview modal.
  useEffect(() => {
    if (open) { openPreview(); onOpenChange?.(false); }
  }, [open, openPreview, onOpenChange]);

  const copyMessage = async () => {
    try { await navigator.clipboard.writeText(`${modal.message}`); toast.success("Message copied — nothing was sent."); }
    catch { toast.error("Couldn't copy. Select and copy manually."); }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const { data: d } = await api.post(`/properties/${propertyId}/documentation-notice/send`, {
        recipient_email: modal.recipient.trim(), subject: modal.subject, message: modal.message,
      });
      setData((c) => ({ ...(c || {}), status: d.status }));
      setModal(null);
      toast.success("Sent. You'll see 'Viewed' here once your landlord opens it.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Email could not be sent."); }
    finally { setSending(false); }
  };

  const dontShowAgain = async () => {
    try { await api.post(`/properties/${propertyId}/documentation-notice/skip`); } catch { /* noop */ }
    setData((c) => ({ ...(c || {}), status: { ...(c?.status || {}), skipped: true } }));
  };

  if (!loaded || !data?.eligible) return null;
  const status = data.status || {};
  const showOffer = status.state === "not_shared" && !status.skipped && !status.revoked && !dismissed;

  return (
    <div data-testid="documentation-notice" className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6 theme-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/10 text-brand shrink-0"><Share2 size={20} /></span>
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-bold text-navy">Share your move-in record</h3>
            <p className="mt-0.5 text-sm text-[#64748B] leading-snug">
              Give your landlord or property manager a shared condition reference for both sides.
            </p>
          </div>
        </div>
        <StatusChip status={status} />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button data-testid="doc-notice-preview-btn" onClick={openPreview} disabled={busy}
          className="h-10 rounded-xl bg-brand hover:bg-brand-hover text-white">
          {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Share2 size={15} className="mr-2" />}
          {status.state === "not_shared" ? "Preview message" : "Share again"}
        </Button>
        {showOffer && (
          <>
            <Button data-testid="doc-notice-notnow-btn" variant="outline" onClick={() => setDismissed(true)}
              className="h-10 rounded-xl border-[#E2E8F0] text-navy hover:bg-[#F8FAFC]">Not now</Button>
            <Button data-testid="doc-notice-skip-btn" variant="ghost" onClick={dontShowAgain}
              className="h-10 rounded-xl text-[#94A3B8] hover:text-navy"><Ban size={14} className="mr-1.5" /> Don't show again</Button>
          </>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="doc-notice-modal" onClick={() => setModal(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 sm:p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h4 className="font-heading text-lg font-bold text-navy">Preview &amp; send</h4>
              <button data-testid="doc-notice-modal-close" onClick={() => setModal(null)} className="text-[#94A3B8] hover:text-navy"><X size={18} /></button>
            </div>
            <label className="mt-4 block text-xs font-semibold text-[#475569]">Recipient email</label>
            <input data-testid="doc-notice-recipient" value={modal.recipient} onChange={(e) => setModal((m) => ({ ...m, recipient: e.target.value }))}
              placeholder="landlord@example.com (optional for copy)"
              className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm outline-none focus:border-brand" />
            <label className="mt-3 block text-xs font-semibold text-[#475569]">Subject</label>
            <input data-testid="doc-notice-subject" value={modal.subject} onChange={(e) => setModal((m) => ({ ...m, subject: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm outline-none focus:border-brand" />
            <label className="mt-3 block text-xs font-semibold text-[#475569]">Message</label>
            <textarea data-testid="doc-notice-body" value={modal.message} onChange={(e) => setModal((m) => ({ ...m, message: e.target.value.slice(0, 2000) }))}
              rows={5} className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm outline-none focus:border-brand resize-none" />
            <p className="mt-2 text-[11px] text-[#94A3B8] break-all" data-testid="doc-notice-url">Shared link: {modal.share_url}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button data-testid="doc-notice-copy-btn" variant="outline" onClick={copyMessage}
                className="h-10 rounded-xl border-[#E2E8F0] text-navy hover:bg-[#F8FAFC]"><Copy size={15} className="mr-2" /> Copy message</Button>
              <Button data-testid="doc-notice-send-btn" onClick={sendEmail} disabled={sending || !modal.recipient.trim()}
                className="h-10 rounded-xl bg-navy hover:bg-navy-hover text-white">
                {sending ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Send size={15} className="mr-2" />} Send by email
              </Button>
            </div>
            <p className="mt-2 text-[11px] text-[#94A3B8]">Nothing sends until you press “Send by email”. Copying the message does not send anything.</p>
          </div>
        </div>
      )}
    </div>
  );
}
